import React, { useState, useEffect } from 'react';
import { useAvailability } from '../hooks/useAvailability';
import DateSelection from './DateSelection';
import StatusLegend from './StatusLegend';
import Footer from './Footer';
import MonthNavigation from './MonthNavigation';
import ViewToggle from './ViewToggle';
import HomeDetails from './HomeDetails';
import Reviews from './Reviews';
import GuestInformation from './GuestInformation';

const Dashboard = ({ isAdmin, user, onLogout }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [viewMode, setViewMode] = useState(isAdmin ? 'admin' : 'user');
  const [currentView, setCurrentView] = useState(isAdmin ? 'calendar' : 'details');
  const [selectedDates, setSelectedDates] = useState(new Set());
  const [reviews, setReviews] = useState([]);
  const [serverTime, setServerTime] = useState(new Date());

  useEffect(() => {
    if (isAdmin) {
      setViewMode('admin');
      setCurrentView('calendar');
    } else {
      setViewMode('user');
      setCurrentView('details');
    }
  }, [isAdmin]);

  useEffect(() => {
    // Fetch reviews data
    fetch('/data/reviews.json')
      .then(response => response.json())
      .then(data => setReviews(data))
      .catch(error => console.error('Error fetching reviews:', error));
  }, []);

  // Fetch server time on mount
  useEffect(() => {
    const fetchServerTime = async () => {
      try {
        const response = await fetch(window.location.href);
        const dateString = response.headers.get('date');
        if (dateString) {
          setServerTime(new Date(dateString));
        }
      } catch (error) {
        console.error('Error fetching server time:', error);
        // Fallback to client time if server time fetch fails
        setServerTime(new Date());
      }
    };
    fetchServerTime();
  }, []);

  const visibleRange = React.useMemo(() => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 12, 0);
    return { start, end };
  }, []);

  const { availability, isLoading, updateAvailability } = useAvailability(visibleRange);

  const changeMonth = (increment) => {
    setCurrentMonth((prev) => {
      const newMonth = new Date(prev.getFullYear(), prev.getMonth() + increment, 1);
      if (newMonth >= visibleRange.start && newMonth <= visibleRange.end) {
        return newMonth;
      }
      return prev;
    });
  };

  const toggleViewMode = () => {
    setViewMode((prevMode) => prevMode === 'admin' ? 'user' : 'admin');
    setSelectedDates(new Set());
  };

    if (isLoading) {
    return <div className="flex justify-center items-center h-screen">Loading...</div>;
  }

  return (
    <div className="flex flex-col min-h-screen bg-gray-100">
      <div className="flex-grow container mx-auto px-4 py-4">
        <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6 mb-4 max-w-6xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2 sm:mb-4">
            <button 
              onClick={onLogout}
              className="order-1 sm:order-2 w-full sm:w-auto bg-gray-200 text-gray-800 py-1.5 sm:py-2 px-4 rounded-md text-sm hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-opacity-50 mb-2 sm:mb-0"
            >
              Logout
            </button>
            <div className="order-2 sm:order-1 w-full sm:w-auto">
              <ViewToggle view={currentView} setView={setCurrentView} />
            </div>
          </div>

          {currentView === 'calendar' && (
            <>
              <MonthNavigation
                currentMonth={currentMonth}
                changeMonth={changeMonth}
                visibleRange={visibleRange}
              />
              <DateSelection
                currentMonth={currentMonth}
                availability={availability}
                isAdmin={isAdmin}
                viewMode={viewMode}
                updateAvailability={updateAvailability}
                userEmail={user.email}
                selectedDates={selectedDates}
                setSelectedDates={setSelectedDates}
                serverTime={serverTime}
              />
              <StatusLegend viewMode={viewMode} />
            </>
          )}

          {currentView === 'details' && (
            <HomeDetails />
          )}

          {currentView === 'reviews' && (
            <Reviews reviews={reviews} />
          )}

          {currentView === 'guestinfo' && (
            <GuestInformation />
          )}

          {isAdmin && (
            <div className="mt-4">
              <button 
                onClick={toggleViewMode}
                className="w-full bg-blue-500 text-white py-2 px-4 rounded-md text-sm hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
              >
                Switch to {viewMode === 'admin' ? 'User' : 'Admin'} View
              </button>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Dashboard;